import { Service, inject } from '@piwikpro/platform';
import { createAction } from 'redux-actions';
import { HttpClient } from '@piwikpro/http-crate';
import { NotifyAboutUpdate } from '@piwikpro/notification-crate';
import type { Store } from 'redux';
import { AppId } from '@piwikpro/apps-crate';
import { IAppScrollTrackingResponse, IScrollTrackingAttributes } from './redux/scrollTracking';
import {
  DomainSettingsAttributes, MultipleSettingsResponse, SingleSettingResponse, DomainSetting,
} from './redux/appDomainSettings';
import { AppInstallationCodeResponse } from './redux/appInstallationCode';
import { IAppSpaTrackingResponse, IAppSpaTrackingSettingData, ISpaTrackingAttributes } from './redux/spaTracking';
import { IAppSecureCookieResponse, ISecureCookieAttributes } from './redux/secureCookie';


export const appContainerSettingFetchInitialized = (settingName?: string) => createAction(
  `@tag/query/appContainerSetting/get/singleParam/${settingName}/init`,
);
export const appContainerSettingFetchSucceeded = (
  settingName?: string,
) => createAction<
IAppScrollTrackingResponse | IAppSpaTrackingSettingData | IAppSecureCookieResponse | SingleSettingResponse,
IAppScrollTrackingResponse | IAppSpaTrackingSettingData | IAppSecureCookieResponse | SingleSettingResponse
>(
  `@tag/query/appContainerSetting/get/singleParam/${settingName}/success`,
  payload => payload,
);
export const appContainerSettingFetchFailed = (settingName: string) => createAction(
  `@tag/query/appContainerSetting/get/singleParam/${settingName}/failed`,
);

export const appContainerSettingsFetchInitialized = createAction(
  '@tag/query/appContainerSetting/get/allParameters/init',
);
export const appContainerSettingsFetchSucceeded = createAction<
MultipleSettingsResponse, MultipleSettingsResponse
>(
  '@tag/query/appContainerSetting/get/allParameters/success',
  payload => payload,
);
export const appContainerSettingsFetchFailed = createAction(
  '@tag/query/appContainerSetting/get/allParameters/failed',
);

export const appDefaultContainerSettingFetchInitialized = createAction(
  '@tag/query/appDefaultSetting/get/allParameters/init',
);
export const appDefaultContainerSettingFetchSucceeded = createAction<
MultipleSettingsResponse, MultipleSettingsResponse
>(
  '@tag/query/appDefaultSetting/get/allParameters/success',
  payload => payload,
);
export const appDefaultContainerSettingFetchFailed = createAction(
  '@tag/query/appDefaultSetting/get/allParameters/failed',
);


export const appContainerSettingUpdateInitialized = (settingName: string) => createAction(
  `@tag/query/appContainerSetting/update/singleParam/${settingName}/init`,
);
export const appContainerSettingUpdateSucceeded = (settingName: string) => createAction<
IAppScrollTrackingResponse | IAppSpaTrackingResponse | IAppSecureCookieResponse | SingleSettingResponse,
IAppScrollTrackingResponse | IAppSpaTrackingResponse | IAppSecureCookieResponse | SingleSettingResponse

>(
  `@tag/query/appContainerSetting/update/singleParam/${settingName}/success`,
  payload => payload,
);
export const appContainerSettingUpdateFailed = (settingName: string) => createAction(
  `@tag/query/appContainerSetting/update/singleParam/${settingName}/failed`,
);
export const appInstallationCodeFetchInitialized = createAction(
  '@tag/query/appContainerSetting/get/installationCode/init',
);
export const appInstallationCodeFetchSucceeded = createAction(
  '@tag/query/appContainerSetting/get/installationCode/success',
  payload => payload,
);
export const appInstallationCodeFetchFailed = createAction(
  '@tag/query/appContainerSetting/get/installationCode/failed',
);

@Service()
export class ContainerSettings {
  private type: string = 'container/app/setting';

  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: NotifyAboutUpdate,
    @inject('config') private readonly config: any,
  ) {}

  async getAppSetting(id: AppId, settingName: DomainSetting) {
    this.store.dispatch(appContainerSettingFetchInitialized(settingName)());

    try {
      // eslint-disable-next-line max-len
      const response:IAppScrollTrackingResponse | IAppSpaTrackingSettingData | IAppSecureCookieResponse | SingleSettingResponse = await this.httpClient.request({
        endpoint: `${this.config.get('CONTAINER_SETTINGS_URL')}/app/${id}/settings/${settingName}`,
        method: 'GET',
      }).jsonApi().send();

      this.store.dispatch(appContainerSettingFetchSucceeded(settingName)(response));

      return response;
    } catch (err: any) {
      this.store.dispatch(appContainerSettingFetchFailed(settingName)());

      throw err;
    }
  }

  async getAppSettings(id: AppId) {
    this.store.dispatch(appContainerSettingsFetchInitialized());

    try {
      const response: MultipleSettingsResponse = await this.httpClient.request({
        endpoint: `${this.config.get('CONTAINER_SETTINGS_URL')}/app/${id}/settings`,
        method: 'GET',
      }).jsonApi().send();


      this.store.dispatch(appContainerSettingsFetchSucceeded(response));

      return response;
    } catch (err: any) {
      this.store.dispatch(appContainerSettingsFetchFailed());

      throw err;
    }
  }

  async updateAppSetting(
    id: AppId,
    settingName: string,
    attributes: IScrollTrackingAttributes | ISpaTrackingAttributes | ISecureCookieAttributes,
  ) {
    this.store.dispatch(appContainerSettingUpdateInitialized(settingName)());

    try {
      await this.httpClient.request({
        endpoint: `${this.config.get('CONTAINER_SETTINGS_URL')}/app/${id}/settings/${settingName}`,
        method: 'PUT',
        body: {
          data: {
            id: settingName,
            type: this.type,
            attributes,
          },
        },
      })
        .intercept(this.notifyAboutUpdate)
        .jsonApi()
        .send();

      this.store.dispatch(appContainerSettingUpdateSucceeded(settingName)(
        {
          data: {
            id: settingName,
            type: this.type,
            attributes,
          },
        },
      ));

      return null;
    } catch (err: any) {
      this.store.dispatch(appContainerSettingUpdateFailed(settingName)());

      throw err;
    }
  }

  async updateAppSettings(
    appId: AppId,
    settings: {
      id: DomainSetting
      attributes: DomainSettingsAttributes
    }[],
  ) {
    settings.forEach(({ id }) => this.store.dispatch(appContainerSettingUpdateInitialized(id)()));

    try {
      await this.httpClient.request({
        endpoint: `${this.config.get('CONTAINER_SETTINGS_URL')}/app/${appId}/settings`,
        method: 'PATCH',
        body: {
          data: settings.map(
            ({ id, attributes }) => ({
              id,
              type: this.type,
              attributes,
            }),
          ),
        },
      })
        .intercept(this.notifyAboutUpdate)
        .jsonApi()
        .send();

      settings.forEach(({ id, attributes }) =>
        this.store.dispatch(appContainerSettingUpdateSucceeded(id)(
          {
            data: {
              id,
              type: this.type,
              attributes,
            },
          },
        )));

      return null;
    } catch (err: any) {
      settings.forEach(({ id }) => this.store.dispatch(appContainerSettingUpdateFailed(id)()));

      throw err;
    }
  }

  async getDefaultAppSettings(appId: AppId) {
    this.store.dispatch(appDefaultContainerSettingFetchInitialized());

    try {
      const response: MultipleSettingsResponse = await this.httpClient.request({
        endpoint: `${this.config.get('CONTAINER_SETTINGS_URL')}/app/${appId}/default-settings`,
        method: 'GET',
      }).jsonApi().send();


      this.store.dispatch(appDefaultContainerSettingFetchSucceeded(response));

      return response;
    } catch (err: any) {
      this.store.dispatch(appDefaultContainerSettingFetchFailed());

      throw err;
    }
  }


  async getAppInstallationCode(id: AppId) {
    this.store.dispatch(appInstallationCodeFetchInitialized());

    try {
      const response: AppInstallationCodeResponse = await this.httpClient.request({
        endpoint: `${this.config.get('CONTAINER_SETTINGS_URL')}/app/${id}/installation-code`,
        method: 'GET',
      }).jsonApi().send();

      this.store.dispatch(appInstallationCodeFetchSucceeded(response));

      return response;
    } catch (err: any) {
      this.store.dispatch(appInstallationCodeFetchFailed());

      throw err;
    }
  }
}
